import React from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useAppStore } from '../../store/useAppStore'
import { extractPlainTextFromMarkdown } from '../../utils/markdownUtils'
import { Dropdown } from '../ui/Dropdown'
import { Icon } from '../ui/Icon'

export type CopyFormat = 'plaintext' | 'markdown' | 'html'

export const CopyButton: React.FC = () => {
  const { t } = useTranslation()
  const { currentDocument } = useAppStore()

  const copyOptions = [
    {
      value: 'plaintext',
      label: t('copyAs.plaintext'),
      icon: <Icon name="document-text" size={16} />
    },
    {
      value: 'markdown',
      label: t('copyAs.markdown'),
      icon: <Icon name="code" size={16} />
    },
    {
      value: 'html',
      label: t('copyAs.html'),
      icon: <Icon name="globe" size={16} />
    }
  ]

  const copyToClipboard = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content)
      return true
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
      return false
    }
  }

  const handleCopyAsPlainText = async () => {
    if (!currentDocument?.content) {
      toast.error(t('copyAs.noContent'))
      return
    }

    const plainText = extractPlainTextFromMarkdown(currentDocument.content)

    const success = await copyToClipboard(plainText)
    if (success) {
      toast.success(t('copyAs.plaintextSuccess'))
    } else {
      toast.error(t('copyAs.copyFailed'))
    }
  }

  const handleCopyAsMarkdown = async () => {
    if (!currentDocument?.content) {
      toast.error(t('copyAs.noContent'))
      return
    }

    const success = await copyToClipboard(currentDocument.content)
    if (success) {
      toast.success(t('copyAs.markdownSuccess'))
    } else {
      toast.error(t('copyAs.copyFailed'))
    }
  }

  const handleCopyAsHTML = async () => {
    const previewElement = document.querySelector('.preview-content') as HTMLElement
    if (!previewElement) {
      toast.error(t('copyAs.noPreview'))
      return
    }

    const html = previewElement.innerHTML
    const success = await copyToClipboard(html)
    if (success) {
      toast.success(t('copyAs.htmlSuccess'))
    } else {
      toast.error(t('copyAs.copyFailed'))
    }
  }

  const handleCopy = (value: string) => {
    const format = value as CopyFormat
    switch (format) {
      case 'plaintext':
        handleCopyAsPlainText()
        break
      case 'markdown':
        handleCopyAsMarkdown()
        break
      case 'html':
        handleCopyAsHTML()
        break
    }
  }

  return (
    <Dropdown
      options={copyOptions}
      value=""
      onChange={handleCopy}
      triggerIcon={<Icon name="copy" size={16} />}
      triggerLabel={t('copyAs.label')}
      hideChevron={true}
      align="right"
    />
  )
}
